import React, { Component, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import BookingLayout from "layouts/Booking";
import { logPageView } from "services/analytics";
import NextRouteURLs from "NextRouteURLs";
import DateField from "components/forms/DateField";
import BookingBanner from "../components/BookingBanner";
import { BookingStep } from "../steps";
import BookingTimePicker, { TimeRange } from "../components/BookingTimePicker";
import { loadBookingTimes } from "services/bookings";
import { loadDraftBooking, saveDraftBooking } from "services/bookings/drafts";
import {
  isDatePricingCalendar,
  loadSettings,
  loadSpacePricingCalendar,
  loadTimeRangePrice,
} from "services/pricing";
import { SpacePricingCalendar } from "services/entities/pricing";
import { DateTime, TimeSpan } from "util/time";
import {
  BookingSettings,
  BookingTimes,
  BookingTimesFilters,
  DraftBooking,
} from "services/entities/booking";
import BookingFooter from "../components/BookingFooter";
import { handleError, NotFoundError } from "util/errors";
import "./index.scss";
import { Button } from "@material-ui/core";
import { AddIcon, DeleteIcon } from "components/Icon";
import { CurrencyAmount } from "util/currency";
import queryString from "querystring";

interface EditBookingTimePageProps
  extends RouteComponentProps<{ code?: string }> {}

interface DraftBookingTime {
  date: DateTime;
  start: TimeSpan | null;
  end: TimeSpan | null;
  price: CurrencyAmount | null;
}

interface EditBookingTimeState {
  pricingCalendar: SpacePricingCalendar | null;
  reservedTimes: {
    start: DateTime;
    end: DateTime;
    times: BookingTimes[];
  } | null;
  dayReservedTimes: TimeRange[] | null;
  booking: DraftBooking | null;
  settings: BookingSettings | null;
  selectedDate: DateTime | null;
  dateAvailable: boolean;
  bookingTimes: DraftBookingTime[];
  selectedBookingTimeIndex: number | null;
}

export default class EditBookingTimePage extends Component<
  EditBookingTimePageProps,
  EditBookingTimeState
> {
  constructor(props: EditBookingTimePageProps) {
    super(props);
    this.state = {
      pricingCalendar: null,
      reservedTimes: null,
      dayReservedTimes: null,
      booking: null,
      settings: null,
      dateAvailable: true,
      bookingTimes: [],
      selectedBookingTimeIndex: null,
      selectedDate: null,
    };
  }

  get selectedBookingTime(): DraftBookingTime | null {
    const bookingTimes = this.state.bookingTimes;
    const selectedTimeIndex = this.state.selectedBookingTimeIndex;
    if (
      selectedTimeIndex === null ||
      selectedTimeIndex >= bookingTimes.length
    ) {
      return null;
    }
    return bookingTimes[selectedTimeIndex];
  }

  get selectedDate(): DateTime {
    let date = this.state.selectedDate;
    if (!date) {
      date = DateTime.now(this.state.settings?.defaultTimeZone).date.add(
        new TimeSpan({ days: 1 })
      );
    }
    return date;
  }

  async componentDidMount() {
    logPageView(this.props.location.pathname, "Edit Booking - Date & Time");
    const bookingCode = this.props.match.params.code;
    try {
      const settings = await loadSettings();
      this.setState({ settings: settings });
    } catch (err) {
      handleError("Failed to load booking settings", err);
    }
    try {
      const booking = await loadDraftBooking(bookingCode);
      if (booking) {
        this.onBookingLoaded(booking);
      } else {
        this.props.history.replace(NextRouteURLs.SPACES);
      }
    } catch (err) {
      console.warn("Failed loading booking", err);
      this.props.history.replace(NextRouteURLs.SPACES);
    }
  }

  onBookingLoaded(booking: DraftBooking) {
    if (!booking.space) {
      this.props.history.replace(NextRouteURLs.SPACES);
      return;
    }
    let bookingTimes: DraftBookingTime[] = [];
    if (booking.times.length > 0) {
      bookingTimes = booking.times.map((t) => {
        return {
          date: t.startTime.date,
          start: t.startTime.time,
          end: t.endTime.time,
          price: null,
        };
      });
      for (const t of bookingTimes) {
        if (t.start && t.end) {
          loadTimeRangePrice(t.date, t.start, t.end, booking.space, true).then(
            (price) => {
              t.price = price;
              this.setState(() => {
                return { bookingTimes: bookingTimes };
              });
            }
          );
        }
      }
    } else {
      const qs = queryString.parse(this.props.location.search.replace("?", ""));
      const dateStr = qs.date as string | undefined;
      const paramDate = dateStr
        ? DateTime.tryParse(dateStr, this.state.settings?.defaultTimeZone)
        : null;
      if (paramDate && this.getBookingDateAvailable(paramDate)) {
        bookingTimes.push({
          date: paramDate,
          start: null,
          end: null,
          price: null,
        });
      }
    }
    let selectedBookingTimeIndex: number | null = null;
    let selectedBookingTime: DraftBookingTime | null = null;
    let selectedDate: DateTime | null = null;
    if (bookingTimes.length > 0) {
      selectedBookingTimeIndex = 0;
      selectedBookingTime = bookingTimes[selectedBookingTimeIndex];
      selectedDate = selectedBookingTime.date;
    }
    this.setState(
      {
        booking: booking,
        bookingTimes: bookingTimes,
        selectedBookingTimeIndex: selectedBookingTimeIndex,
        selectedDate: selectedDate,
      },
      () => {
        if (this.state.settings && selectedBookingTime) {
          this.setState({
            dateAvailable: this.getBookingDateAvailable(
              selectedBookingTime.date
            ),
          });
        } else {
          this.setState({ dateAvailable: false });
        }
      }
    );
    if (selectedBookingTime) {
      this.loadPricingCalendar(booking.space, selectedBookingTime.date);
      this.loadDayBookingTimes(booking, selectedBookingTime.date);
    }
  }

  updateBooking() {
    const booking = this.state.booking;
    if (!booking) {
      return;
    }
    const times = this.state.bookingTimes;
    booking.times = times
      .filter((t) => t.start && t.end)
      .map((t, i) => {
        const includeFlexiTicket =
          i < booking.times.length
            ? booking.times[i].includeFlexiTicket
            : false;
        return {
          startTime: t.date.add(t.start!),
          endTime: t.date.add(t.end!),
          includeFlexiTicket: includeFlexiTicket,
        };
      });
    this.setState(() => {
      return { booking: booking };
    });
    saveDraftBooking(booking);
  }

  getBookingDateAvailable(date: DateTime): boolean {
    const settings = this.state.settings;
    if (!settings) {
      throw new Error("Booking not loaded");
    }
    const minDate = DateTime.today(settings.defaultTimeZone).add(
      new TimeSpan({ days: settings.minDateOffset })
    );
    return !date.lessThan(minDate);
  }

  loadPricingCalendar(space: string, date: DateTime) {
    loadSpacePricingCalendar(space, date).then(
      (calendar) => this.setState({ pricingCalendar: calendar }),
      (err) => {
        if (err instanceof NotFoundError) {
          this.setState({ pricingCalendar: null });
        } else {
          console.warn("Failed to load pricing calendar", err);
          this.setState({ pricingCalendar: null });
        }
      }
    );
  }

  loadDayBookingTimes(booking: DraftBooking, date: DateTime) {
    const monthBookingTimes = this.state.reservedTimes;
    if (
      monthBookingTimes &&
      !date.lessThan(monthBookingTimes.start) &&
      !date.greaterThan(monthBookingTimes.end)
    ) {
      // can reuse the previous booking times
      this.setState({
        dayReservedTimes: EditBookingTimePage.getDayBookingTimes(
          date,
          monthBookingTimes.times
        ),
      });
      return;
    }
    this.setState({ dayReservedTimes: null });
    const minTime = date.startOf("month");
    const maxTime = date.endOf("month");
    const filters: BookingTimesFilters = {
      minTime: minTime,
      maxTime: maxTime,
      ignoreCode: booking.code || undefined,
      space: booking.space,
    };
    loadBookingTimes(filters).then(
      (times) =>
        this.setState({
          reservedTimes: { start: minTime, end: maxTime, times: times },
          dayReservedTimes: EditBookingTimePage.getDayBookingTimes(date, times),
        }),
      (err) => handleError("Failed to load booking times", err)
    );
  }

  static getDayBookingTimes(
    date: DateTime,
    bookingTimes: BookingTimes[]
  ): TimeRange[] {
    const min = date.startOf("day");
    const max = date.endOf("day");
    return bookingTimes
      .filter((t) => !t.end.lessThan(min) && !t.start.greaterThan(max))
      .map((t) => {
        return { start: t.start.time, end: t.end.time };
      });
  }

  getDayBookedTimes(date: DateTime): TimeRange[] {
    const times: DraftBookingTime[] = this.state.bookingTimes;
    const selectedIndex = this.state.selectedBookingTimeIndex;
    return times
      .filter(
        (t, i) =>
          i !== selectedIndex && t.start && t.end && t.date.equals(date, "day")
      )
      .map((t) => {
        return { start: t.start!, end: t.end! };
      });
  }

  onDateChange = (date: DateTime | null) => {
    const booking = this.state.booking;
    if (!date) {
      return;
    } else if (!booking) {
      console.warn("Booking not loaded");
      return;
    }
    const settings = this.state.settings;
    if (!settings) {
      console.warn("Settings not loaded");
      return;
    }
    date = date.withTimeZone(settings.defaultTimeZone, true);
    if (!this.getBookingDateAvailable(date)) {
      this.setState({ dateAvailable: false });
      return;
    }
    const currentCalendar = this.state.pricingCalendar;
    if (
      booking.space &&
      (!currentCalendar || !isDatePricingCalendar(date, currentCalendar))
    ) {
      // current calendar is still not valid - load new one
      this.loadPricingCalendar(booking.space, date);
    }
    let selectedTime = this.selectedBookingTime;
    const bookingTimes = this.state.bookingTimes;
    let selectedTimeIndex = this.state.selectedBookingTimeIndex;
    if (selectedTime) {
      selectedTime.date = date;
      selectedTime.start = selectedTime.end = null;
    } else {
      // no date selected - create new time
      selectedTime = { date: date, start: null, end: null, price: null };
      bookingTimes.push(selectedTime);
      selectedTimeIndex = bookingTimes.length - 1;
    }
    this.setState({
      bookingTimes: bookingTimes,
      selectedBookingTimeIndex: selectedTimeIndex,
      dateAvailable: true,
      selectedDate: date,
    });
    this.loadDayBookingTimes(booking, date);
    this.updateBooking();
  };

  onTimeChange = (value: { start: TimeSpan; end: TimeSpan } | null) => {
    let selectedTime = this.selectedBookingTime;
    const bookingTimes = this.state.bookingTimes;
    let selectedTimeIndex = this.state.selectedBookingTimeIndex;
    if (!selectedTime) {
      selectedTime = {
        date: this.selectedDate,
        start: null,
        end: null,
        price: null,
      };
      bookingTimes.push(selectedTime);
      selectedTimeIndex = bookingTimes.length - 1;
    }
    const booking = this.state.booking;
    selectedTime.start = value ? value.start : null;
    selectedTime.end = value ? value.end : null;
    this.setState({
      bookingTimes: this.state.bookingTimes,
      selectedBookingTimeIndex: selectedTimeIndex,
    });
    if (booking && selectedTime.start && selectedTime.end) {
      loadTimeRangePrice(
        selectedTime.date,
        selectedTime.start,
        selectedTime.end,
        booking.space,
        true
      ).then((price) => {
        selectedTime!.price = price;
        this.setState({ bookingTimes: this.state.bookingTimes });
      });
    }
    this.updateBooking();
  };

  onAddTimeSlotClick = () => {
    let date = this.state.selectedDate;
    if (!date) {
      date = DateTime.now(this.state.settings?.defaultTimeZone).date.add(
        new TimeSpan({ days: 1 })
      );
    }
    const bookingTimes = this.state.bookingTimes;
    bookingTimes.push({ date: date, start: null, end: null, price: null });
    this.setState({
      bookingTimes: bookingTimes,
      selectedBookingTimeIndex: bookingTimes.length - 1,
    });
  };

  onBookingTimeClick = (ev: MouseEvent, index: number) => {
    ev.preventDefault();
    ev.stopPropagation();
    this.setState({ selectedBookingTimeIndex: index });
  };

  onDeleteBookingTimeClick = (ev: MouseEvent, index: number) => {
    ev.preventDefault();
    ev.stopPropagation();
    let selectedTimeIndex = this.state.selectedBookingTimeIndex;
    const bookingTimes = this.state.bookingTimes;
    bookingTimes.splice(index, 1);
    if (
      selectedTimeIndex !== null &&
      selectedTimeIndex >= bookingTimes.length
    ) {
      selectedTimeIndex = bookingTimes.length - 1;
    }
    this.setState({
      bookingTimes: bookingTimes,
      selectedBookingTimeIndex: selectedTimeIndex,
    });
    this.updateBooking();
  };

  render() {
    const booking = this.state.booking;
    const bookingTimes = this.state.bookingTimes;
    const isNewBooking: boolean = !booking || !booking.code;
    const selectedTime = this.selectedBookingTime;
    const selectedTimeIndex = this.state.selectedBookingTimeIndex;
    const defaultDate = selectedTime ? selectedTime.date : undefined;
    const timeValue =
      selectedTime && selectedTime.start && selectedTime.end
        ? { start: selectedTime.start, end: selectedTime.end }
        : undefined;
    const settings = this.state.settings;
    const daySelectedTimes = selectedTime
      ? this.getDayBookedTimes(selectedTime.date)
      : undefined;
    return (
      <BookingLayout
        id="create-booking-time-page"
        footerChildren={
          <BookingFooter booking={booking} currentStep={BookingStep.Time} />
        }
      >
        <BookingBanner
          title="Date & Time"
          subtitle="Select the date and time of your booking"
          currentStep={BookingStep.Time}
          booking={booking}
        />
        <div className="date-time">
          <div className="date">
            <DateField
              inline={true}
              disablePast={true}
              name="date"
              defaultValue={defaultDate}
              onChange={this.onDateChange}
              disabled={!isNewBooking}
              timeZone={settings ? settings.defaultTimeZone : undefined}
            />
            <div className="times">
              <h3>My times</h3>
              {bookingTimes &&
                bookingTimes.map((t, i) => {
                  return (
                    <div
                      key={i}
                      className={
                        i === selectedTimeIndex
                          ? "booking-time selected"
                          : "booking-time"
                      }
                      onClick={(ev) => this.onBookingTimeClick(ev, i)}
                    >
                      <span className="date">
                        {t.date.format("dddd D MMMM")}
                      </span>
                      {t.start && t.end && (
                        <span className="time">
                          {t.start.format("h:mmA")} - {t.end.format("h:mmA")}
                        </span>
                      )}
                      {t.price && (
                        <span className="price">{t.price.format("$0")}</span>
                      )}
                      <button
                        className="delete"
                        onClick={(ev) => this.onDeleteBookingTimeClick(ev, i)}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  );
                })}
              <Button className="add-time" onClick={this.onAddTimeSlotClick}>
                <AddIcon /> Add another time slot
              </Button>
            </div>
          </div>
          {this.state.dateAvailable ? (
            <div className="time-field">
              <BookingTimePicker
                pricingCalendar={this.state.pricingCalendar || undefined}
                onChange={this.onTimeChange}
                reservedTimes={this.state.dayReservedTimes || undefined}
                selectedTimes={daySelectedTimes}
                disabled={!isNewBooking}
                value={timeValue}
                date={
                  selectedTime && selectedTime.date
                    ? selectedTime.date
                    : this.selectedDate
                }
              />
            </div>
          ) : (
            <div className="date-unavailable">
              <p>Too close to current date.</p>
              <p>
                <Link to={NextRouteURLs.CONTACT}>Contact us</Link> to check for
                availability.
              </p>
            </div>
          )}
        </div>
      </BookingLayout>
    );
  }
}
